import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Feature } from 'src/WebApiController';

import { useUserHasFeature } from './useUserHasFeature';

export type ReturnStateWithViagVirtId = {
  fromViagVirtId?: string;
};

export const useScrollToEventIndexFromBackSection = () => {
  const { eventsTransformed } = useCatalogDataContext();
  const location = useLocation();

  const hasRememberScrollPositionEventListFeature = useUserHasFeature(
    Feature.RememberScrollPositionEventList
  );

  const [returningFromViagVirtId, setReturningFromViagVirtId] =
    useState<string>();

  useEffect(() => {
    if (hasRememberScrollPositionEventListFeature) {
      const fromViagVirtId = (location.state as ReturnStateWithViagVirtId)
        ?.fromViagVirtId;
      if (fromViagVirtId && fromViagVirtId !== returningFromViagVirtId) {
        setReturningFromViagVirtId(fromViagVirtId);
      }
    }
  }, [
    hasRememberScrollPositionEventListFeature,
    location.state,
    returningFromViagVirtId,
  ]);

  const scrollToItemIndex = useMemo(() => {
    if (
      returningFromViagVirtId == null ||
      eventsTransformed == null ||
      !hasRememberScrollPositionEventListFeature
    ) {
      return undefined;
    }

    const scrollToIndex = eventsTransformed.findIndex(
      (e) => e.event.virtId === returningFromViagVirtId
    );
    // Not found means the event is not in the list, so we don't want to scroll to it any more
    if (scrollToIndex === -1) {
      setReturningFromViagVirtId(undefined);
    }

    return scrollToIndex === -1 ? undefined : scrollToIndex;
  }, [
    eventsTransformed,
    hasRememberScrollPositionEventListFeature,
    returningFromViagVirtId,
  ]);

  return { returningFromViagVirtId, scrollToItemIndex };
};
